import React, { useCallback, useMemo, useRef, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  defaultPreservationGridSort,
  strapiApproveWorkOrderRps,
  strapiListBeritaAcaraPreservationForms,
  strapiReorderBeritaAcaraLetters,
  strapiRejectWorkOrderRps,
  strapiSamplePreservationReceived,
  type BeritaAcaraPreservationFormRow,
  type PreservationGridSort,
  type PreservationGridSortField,
  type PreservationRpsStatus,
  type StrapiPreservationGridRow,
} from '../../../../api/strapiCrm';
import type { StrapiManagedUserRow } from '../../../../api/strapiUsers';
import { useAuth } from '../../../../auth/AuthContext';
import { DataGridNumberedPagination, DATA_GRID_DEFAULT_PAGE_SIZE, DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS } from '../../../../components/DataGridNumberedPagination';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { usePreservationGridRowsQuery, useUsersQuery } from '../../../../query/hooks';
import { queryKeys } from '../../../../query/queryKeys';
import {
  SamplePreservationSignaturePad,
  type SamplePreservationSignaturePadHandle,
} from '../../components/SamplePreservationSignaturePad';
import {
  canApproveRps,
  canGenerateRpsPdf,
  canPrintIssuedBeritaAcara,
  canRejectRps,
  canSampleReceived,
  canShowBlockedSampleReceived,
  canShowVerifyFormSampling,
  canShowViewVerifiedFormSampling,
  canViewReceiveLogs,
  formSamplingAllVerified,
  sampleReceiveBlockedReason,
  hasVisiblePreservationActions,
  isPetugasSampling,
  isSpvLaboratorium,
  receivedByAdminLabel,
} from '../../preservationRowActions';
import { SamplePreservationCombinedPdfPreviewDialog } from '../../components/SamplePreservationCombinedPdfPreviewDialog';
import { SurveyBeritaAcaraPdfPreviewDialog } from '../../../SurveyPage/components/SurveyBeritaAcaraPdfPreviewDialog';
import { SamplePreservationReceiveLogsDialog } from '../../components/SamplePreservationReceiveLogsDialog';
import { VerifyFormSamplingDialog } from '../../components/VerifyFormSamplingDialog';

const RPS_STATUS_LABEL: Record<PreservationRpsStatus, string> = {
  pending: 'Belum ditinjau',
  approved: 'Approved',
  rejected: 'RPS ditolak',
};

const RPS_STATUS_CHIP: Record<PreservationRpsStatus, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

function PreservationActionIcon({
  title,
  ariaLabel,
  testId,
  color,
  onClick,
  children,
}: {
  title: string;
  ariaLabel: string;
  testId: string;
  color?: 'default' | 'success' | 'error';
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <IconButton size="small" color={color} aria-label={ariaLabel} data-testid={testId} onClick={onClick}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

const COMPLETION_LABEL: Record<StrapiPreservationGridRow['completion_status'], string> = {
  not_ready: 'Belum siap',
  partial_completed: 'Partial completed',
  all_completed: 'All completed',
};

const WORKFLOW_LABEL: Record<StrapiPreservationGridRow['workflow_step'], string> = {
  awaiting_signatures: 'Menunggu tanda tangan',
  survey_in_progress: 'Sampling in progress',
  all_completed: 'Siap serah terima',
  received: 'Diterima kantor',
  verified: 'Terverifikasi',
  preserved: 'Pengawetan selesai',
};

function userLabel(u: StrapiManagedUserRow | null): string {
  if (!u) return '';
  const name = u.username?.trim() || u.email?.trim();
  const id = u.numericId != null ? ` #${u.numericId}` : '';
  return `${name}${id}`;
}

function toDatetimeLocalValue(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string {
  if (!value.trim()) return new Date().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function toDateInputValue(value?: string | null): string {
  const raw = String(value ?? '').trim();
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  if (m) return m[1];
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
  }
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function moveReceiveForm(
  rows: BeritaAcaraPreservationFormRow[],
  fromId: string,
  toId: string,
): BeritaAcaraPreservationFormRow[] | null {
  if (!fromId || !toId || fromId === toId) return null;
  const fromIdx = rows.findIndex((row) => row.berita_acara_document_id === fromId);
  const toIdx = rows.findIndex((row) => row.berita_acara_document_id === toId);
  if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return null;
  const next = [...rows];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  let order = 1;
  return next.map((row) => {
    if (!row.berita_acara_document_id) return row;
    const listOrder = order;
    order += 1;
    const job = row.sample_code.includes('-')
      ? row.sample_code.slice(0, row.sample_code.lastIndexOf('-'))
      : '';
    return {
      ...row,
      list_order: listOrder,
      sample_code: job ? `${job}-${listOrder}` : '',
    };
  });
}

function beritaAcaraCheckboxLabel(form: BeritaAcaraPreservationFormRow): string {
  const code =
    form.sample_code ||
    (form.list_order != null ? String(form.list_order) : form.point_label);
  const parts = [
    code,
    form.jenis_pengukuran_label,
    form.location_name,
  ].filter((s) => Boolean(String(s ?? '').trim()));
  return parts.join(' — ') || form.berita_acara_document_id || form.point_label;
}

function issuedReceiveForms(forms: BeritaAcaraPreservationFormRow[]): BeritaAcaraPreservationFormRow[] {
  return forms.filter((form) => Boolean(form.berita_acara_document_id));
}

function compareReceiveFormsBySampleCode(
  a: BeritaAcaraPreservationFormRow,
  b: BeritaAcaraPreservationFormRow,
): number {
  const ao = a.list_order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.list_order ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return a.point_index - b.point_index;
}

function receiveFormKey(form: BeritaAcaraPreservationFormRow): string {
  return (
    form.berita_acara_document_id ||
    `pending:${form.sampling_plan_document_id}:${form.point_index}`
  );
}

const DEFAULT_PRESERVATION_SORT_MODEL: GridSortModel = [
  { field: defaultPreservationGridSort.field, sort: defaultPreservationGridSort.direction },
];

const PRESERVATION_SORT_FIELDS = new Set<string>([
  'berita_acara_created_at',
  'work_order_job_number',
  'work_order_label',
  'signed_points',
  'total_points',
  'handed_over_points',
  'completion_status',
  'workflow_step',
  'sample_received_datetime',
  'sample_verified_datetime',
  'rps_status',
  'rps_reviewed_datetime',
]);

function gridSortModelToPreservationSort(model: GridSortModel): PreservationGridSort {
  const first = model[0];
  if (first?.field && first.sort && PRESERVATION_SORT_FIELDS.has(first.field)) {
    return {
      field: first.field as PreservationGridSortField,
      direction: first.sort,
    };
  }
  return { ...defaultPreservationGridSort };
}

export function SamplePreservationListTabScreen() {
  const { jwt, user } = useAuth();
  const qc = useQueryClient();

  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_PRESERVATION_SORT_MODEL);
  const appliedSort = useMemo(() => gridSortModelToPreservationSort(sortModel), [sortModel]);
  const rowsQ = usePreservationGridRowsQuery(jwt, appliedSort);

  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'active' });
  const userOptions = useMemo(
    () => (usersQ.data?.rows ?? []).filter((u) => u.numericId != null),
    [usersQ.data?.rows],
  );
  const adminPenerimaOptions = useMemo(
    () =>
      userOptions.filter((u) =>
        ['admin_penerima_sample', 'superadmin'].includes(String(u.userTypeCode ?? '').trim().toLowerCase()),
      ),
    [userOptions],
  );

  const [receiveRow, setReceiveRow] = useState<StrapiPreservationGridRow | null>(null);
  const [receiveForms, setReceiveForms] = useState<BeritaAcaraPreservationFormRow[]>([]);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [selectedReceiveIds, setSelectedReceiveIds] = useState<string[]>([]);
  const [receiveHasStroke, setReceiveHasStroke] = useState(false);
  const receiveSignRef = useRef<SamplePreservationSignaturePadHandle | null>(null);
  const [receivedAtLocal, setReceivedAtLocal] = useState(() => toDatetimeLocalValue());
  const [samplingDate, setSamplingDate] = useState(() => toDateInputValue());
  const [sampleReceivedDate, setSampleReceivedDate] = useState(() => toDateInputValue());
  const [receivedBy, setReceivedBy] = useState<StrapiManagedUserRow | null>(null);
  const [preservationByCustomer, setPreservationByCustomer] = useState(false);
  const [preservationByLaboratory, setPreservationByLaboratory] = useState(false);
  const [conditionTidakDiawetkan, setConditionTidakDiawetkan] = useState(false);
  const [conditionWadahTidakSesuai, setConditionWadahTidakSesuai] = useState(false);
  const [conditionKadaluarsa, setConditionKadaluarsa] = useState(false);
  const [conditionLainnya, setConditionLainnya] = useState(false);
  const [conditionLainnyaNote, setConditionLainnyaNote] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [combinedPdfRow, setCombinedPdfRow] = useState<StrapiPreservationGridRow | null>(null);
  const [beritaAcaraPdfRow, setBeritaAcaraPdfRow] = useState<StrapiPreservationGridRow | null>(null);
  const [receiveLogsRow, setReceiveLogsRow] = useState<StrapiPreservationGridRow | null>(null);
  const [verifyFormRow, setVerifyFormRow] = useState<StrapiPreservationGridRow | null>(null);
  const [verifyFormReadOnly, setVerifyFormReadOnly] = useState(false);
  const [draggingReceiveId, setDraggingReceiveId] = useState<string | null>(null);
  const [rpsActionError, setRpsActionError] = useState<string | null>(null);
  const [rpsConfirm, setRpsConfirm] = useState<{
    action: 'approve' | 'reject';
    row: StrapiPreservationGridRow;
  } | null>(null);
  const [rpsRejectNote, setRpsRejectNote] = useState('');
  const [rpsApproveHasStroke, setRpsApproveHasStroke] = useState(false);
  const rpsApproveSignRef = useRef<SamplePreservationSignaturePadHandle | null>(null);

  const invalidate = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.preservationGridRowsPrefix(jwt) });
  }, [qc, jwt]);

  const receiveM = useMutation({
    mutationFn: async () => {
      if (!jwt || !receiveRow || receivedBy?.numericId == null) {
        throw new Error('Data tidak lengkap.');
      }
      if (!selectedReceiveIds.length) throw new Error('Pilih minimal satu berita acara.');
      const sign = receiveSignRef.current?.exportPng()?.trim() ?? '';
      if (!sign) throw new Error('Tanda tangan wajib.');
      if (conditionLainnya && !conditionLainnyaNote.trim()) {
        throw new Error('Sebutkan kondisi sampel lainnya.');
      }
      if (!samplingDate.trim()) throw new Error('Tanggal sampling wajib.');
      if (!sampleReceivedDate.trim()) throw new Error('Tanggal sample diterima wajib.');
      return strapiSamplePreservationReceived(jwt, {
        work_order_document_id: receiveRow.work_order_document_id,
        sample_received_datetime: fromDatetimeLocalValue(receivedAtLocal),
        sample_received_by_user_id: receivedBy.numericId,
        rps_sampling_date: samplingDate,
        rps_sample_received_date: sampleReceivedDate,
        berita_acara_document_ids: selectedReceiveIds,
        sign,
        preservation_by_customer: preservationByCustomer,
        preservation_by_laboratory: preservationByLaboratory,
        received_condition_tidak_diawetkan_di_lapangan: conditionTidakDiawetkan,
        received_condition_wadah_tidak_sesuai: conditionWadahTidakSesuai,
        received_condition_kadaluarsa: conditionKadaluarsa,
        received_condition_lainnya: conditionLainnya,
        received_condition_note: conditionLainnya ? conditionLainnyaNote.trim() : '',
      });
    },
    onSuccess: async () => {
      const woDocId = receiveRow?.work_order_document_id?.trim() ?? '';
      setReceiveRow(null);
      setReceiveForms([]);
      setSelectedReceiveIds([]);
      setReceiveHasStroke(false);
      setModalError(null);
      await invalidate();
      await qc.invalidateQueries({ queryKey: queryKeys.workOrdersListPrefix(jwt) });
      if (woDocId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, woDocId) });
      }
    },
    onError: (e) => setModalError(e instanceof Error ? e.message : 'Gagal menerima sample.'),
  });

  const rpsReviewM = useMutation({
    mutationFn: async () => {
      if (!jwt || !rpsConfirm) throw new Error('Data tidak lengkap.');
      const woId = rpsConfirm.row.work_order_document_id?.trim() ?? '';
      if (!woId) throw new Error('Work order tidak ditemukan.');
      if (rpsConfirm.action === 'approve') {
        const sign = rpsApproveSignRef.current?.exportPng()?.trim() ?? '';
        if (!sign) throw new Error('Tanda tangan SPV laboratorium wajib.');
        return strapiApproveWorkOrderRps(jwt, woId, sign);
      }
      const note = rpsRejectNote.trim();
      if (!note) throw new Error('Catatan penolakan wajib.');
      return strapiRejectWorkOrderRps(jwt, woId, note);
    },
    onSuccess: async () => {
      const approvedRow =
        rpsConfirm?.action === 'approve'
          ? { ...rpsConfirm.row, rps_status: 'approved' as const }
          : null;
      setRpsConfirm(null);
      setRpsRejectNote('');
      setRpsApproveHasStroke(false);
      setRpsActionError(null);
      await invalidate();
      if (approvedRow) setCombinedPdfRow(approvedRow);
    },
    onError: (e) => setRpsActionError(e instanceof Error ? e.message : 'Gagal meninjau RPS.'),
  });

  async function openReceiveModal(row: StrapiPreservationGridRow) {
    if (!jwt) return;
    setModalError(null);
    setReceivedAtLocal(toDatetimeLocalValue());
    setSamplingDate(toDateInputValue(row.rps_sampling_date));
    setSampleReceivedDate(toDateInputValue(row.rps_sample_received_date));
    const defaultUser =
      adminPenerimaOptions.find((u) => u.numericId === user?.id) ?? adminPenerimaOptions[0] ?? null;
    setReceivedBy(defaultUser);
    setPreservationByCustomer(false);
    setPreservationByLaboratory(false);
    setConditionTidakDiawetkan(false);
    setConditionWadahTidakSesuai(false);
    setConditionKadaluarsa(false);
    setConditionLainnya(false);
    setConditionLainnyaNote('');
    setReceiveForms([]);
    setSelectedReceiveIds([]);
    setReceiveRow(row);
    setReceiveLoading(true);
    try {
      const forms = issuedReceiveForms(
        await strapiListBeritaAcaraPreservationForms(jwt, row.work_order_document_id),
      ).sort(compareReceiveFormsBySampleCode);
      setReceiveForms(forms);
      setSelectedReceiveIds(
        forms
          .filter((f) => f.is_fully_signed && f.verified && !f.handed_over_to_office_at)
          .map((f) => f.berita_acara_document_id),
      );
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Gagal memuat berita acara.');
      setReceiveForms([]);
    } finally {
      setReceiveLoading(false);
    }
  }

  const receiveOrderLocked = receiveForms.some((form) => Boolean(form.handed_over_to_office_at));
  const canReorderReceiveCodes = receiveRow?.sample_pickup_method === 'diantar' && !receiveOrderLocked;

  const reorderReceiveM = useMutation({
    mutationFn: async (next: BeritaAcaraPreservationFormRow[]) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const woDocId = receiveRow?.work_order_document_id?.trim() ?? '';
      if (!woDocId) throw new Error('Work order tidak valid.');
      await strapiReorderBeritaAcaraLetters(
        jwt,
        woDocId,
        next.map((form) => form.berita_acara_document_id).filter(Boolean),
      );
    },
    onError: (e) => {
      setModalError(e instanceof Error ? e.message : 'Gagal mengatur urutan kode sampel.');
      if (receiveRow && jwt) {
        void strapiListBeritaAcaraPreservationForms(jwt, receiveRow.work_order_document_id)
          .then((forms) =>
            setReceiveForms(issuedReceiveForms(forms).sort(compareReceiveFormsBySampleCode)),
          )
          .catch(() => undefined);
      }
    },
  });

  const rows = rowsQ.data ?? [];

  const petugasView = isPetugasSampling(user);
  const showActionsColumn = rows.some((row) => hasVisiblePreservationActions(row, user));

  const columns: GridColDef<StrapiPreservationGridRow>[] = useMemo(
    () => {
      const cols: GridColDef<StrapiPreservationGridRow>[] = [
      {
        field: 'work_order_job_number',
        headerName: 'GL job',
        minWidth: 120,
        flex: 0.6,
        renderCell: ({ row }) => (
          <Box data-testid={`sample-preservation-row-${row.work_order_document_id}`}>
            {row.work_order_job_number || row.work_order_label || row.work_order_document_id || '—'}
          </Box>
        ),
      },
      {
        field: 'berita_acara_created_at',
        headerName: 'Tanggal BA',
        minWidth: 160,
        width: 170,
        type: 'dateTime',
        sortingOrder: ['desc', 'asc'],
        valueGetter: (_, row) => {
          const raw = String(row.berita_acara_created_at ?? '').trim();
          if (!raw) return null;
          const d = new Date(raw);
          return Number.isNaN(d.getTime()) ? null : d;
        },
        renderCell: ({ row }) => {
          const raw = String(row.berita_acara_created_at ?? '').trim();
          if (!raw) return '—';
          const d = new Date(raw);
          if (Number.isNaN(d.getTime())) return '—';
          return d.toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' });
        },
      },
      { field: 'work_order_label', headerName: 'Work order', minWidth: 160, flex: 1 },
      {
        field: 'signed_points',
        headerName: 'Berita Acara',
        width: 220,
        valueGetter: (_, row) =>
          `${row.signed_points} / ${row.total_points} - ${COMPLETION_LABEL[row.completion_status] ?? row.completion_status}`,
      },
      {
        field: 'workflow_step',
        headerName: 'Tahap pengamanan',
        width: 150,
        valueGetter: (_, row) => WORKFLOW_LABEL[row.workflow_step] ?? row.workflow_step,
      },
      {
        field: 'received_by_admin',
        headerName: 'Diterima kantor',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => receivedByAdminLabel(row),
      },
      {
        field: 'rps_status',
        headerName: 'Status RPS',
        width: 140,
        sortable: false,
        renderCell: ({ row }) =>
          row.preservation_document_id || row.completion_status === 'all_completed' ? (
            <Chip
              size="small"
              color={RPS_STATUS_CHIP[row.rps_status] ?? 'warning'}
              label={RPS_STATUS_LABEL[row.rps_status] ?? row.rps_status}
              title={row.rps_status === 'rejected' ? row.rps_reject_note || undefined : undefined}
            />
          ) : (
            '—'
          ),
      },
      ];
      if (showActionsColumn) {
        cols.push({
          field: 'actions',
          headerName: 'Aksi',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          minWidth: petugasView ? 72 : 248,
          width: petugasView ? 72 : 248,
          flex: 0,
          align: 'center',
          headerAlign: 'center',
          renderCell: ({ row }) => (
            <Box
              sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {canShowVerifyFormSampling(row, user) ? (
                <Tooltip title="Verifikasi Form Sampling">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={formSamplingAllVerified(row)}
                      aria-label={`Verifikasi Form Sampling ${row.work_order_job_number || row.work_order_document_id}`}
                      data-testid={`sample-preservation-verify-form-sampling-${row.work_order_document_id}`}
                      onClick={() => {
                        setVerifyFormReadOnly(false);
                        setVerifyFormRow(row);
                      }}
                    >
                      <VerifiedOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {canShowViewVerifiedFormSampling(row, user) ? (
                <Tooltip title="Lihat Form Sampling">
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={`Lihat Form Sampling ${row.work_order_job_number || row.work_order_document_id}`}
                    data-testid={`sample-preservation-view-form-sampling-${row.work_order_document_id}`}
                    onClick={() => {
                      setVerifyFormReadOnly(true);
                      setVerifyFormRow(row);
                    }}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canSampleReceived(row, user) ? (
                <Tooltip title="Sample received">
                  <IconButton
                    size="small"
                    aria-label={`Sample received ${row.work_order_job_number || row.work_order_document_id}`}
                    data-testid={`sample-preservation-sample-received-${row.work_order_document_id}`}
                    onClick={() => void openReceiveModal(row)}
                  >
                    <MoveToInboxOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : canShowBlockedSampleReceived(row, user) ? (
                <Tooltip title={sampleReceiveBlockedReason(row) ?? 'Penerimaan sampel diblokir.'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      aria-label={`Sample received diblokir ${row.work_order_job_number || row.work_order_document_id}`}
                      data-testid={`sample-preservation-sample-received-blocked-${row.work_order_document_id}`}
                    >
                      <MoveToInboxOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {canPrintIssuedBeritaAcara(row, user) ? (
                <Tooltip title="Print berita acara">
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Print berita acara ${row.work_order_job_number || row.work_order_document_id}`}
                    data-testid={`sample-preservation-berita-acara-pdf-${row.work_order_document_id}`}
                    onClick={() => setBeritaAcaraPdfRow(row)}
                  >
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canGenerateRpsPdf(row, user) ? (
                <Tooltip
                  title={
                    isSpvLaboratorium(user) && row.rps_status !== 'approved'
                      ? 'Review PDF'
                      : 'Lihat PDF'
                  }
                >
                  <IconButton
                    size="small"
                    aria-label={`PDF RPS & Permohonan Pengujian ${row.work_order_job_number || row.work_order_document_id}`}
                    data-testid={`sample-preservation-combined-pdf-${row.work_order_document_id}`}
                    onClick={() => setCombinedPdfRow(row)}
                  >
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canViewReceiveLogs(user) ? (
                <PreservationActionIcon
                  title="Log penerimaan"
                  ariaLabel={`Log penerimaan ${row.work_order_job_number || row.work_order_document_id}`}
                  testId={`sample-preservation-receive-logs-${row.work_order_document_id}`}
                  onClick={() => setReceiveLogsRow(row)}
                >
                  <HistoryOutlinedIcon fontSize="small" />
                </PreservationActionIcon>
              ) : null}
              {!petugasView && canApproveRps(row, user) ? (
                <PreservationActionIcon
                  title="Setujui RPS"
                  ariaLabel={`Setujui RPS ${row.work_order_job_number || row.work_order_document_id}`}
                  testId={`sample-preservation-approve-rps-${row.work_order_document_id}`}
                  color="success"
                  onClick={() => {
                    setRpsActionError(null);
                    setRpsRejectNote('');
                    setRpsApproveHasStroke(false);
                    setRpsConfirm({ action: 'approve', row });
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </PreservationActionIcon>
              ) : null}
              {!petugasView && canRejectRps(row, user) ? (
                <PreservationActionIcon
                  title="Tolak RPS"
                  ariaLabel={`Tolak RPS ${row.work_order_job_number || row.work_order_document_id}`}
                  testId={`sample-preservation-reject-rps-${row.work_order_document_id}`}
                  color="error"
                  onClick={() => {
                    setRpsActionError(null);
                    setRpsRejectNote('');
                    setRpsConfirm({ action: 'reject', row });
                  }}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </PreservationActionIcon>
              ) : null}
            </Box>
          ),
        });
      }
      return cols;
    },
    [petugasView, showActionsColumn, user, adminPenerimaOptions],
  );

  const gridRows = useMemo(() => rows.map((r) => ({ ...r, id: r.row_key })), [rows]);

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CollapsibleListFilters
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={rowsQ.isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!jwt || rowsQ.isFetching}
            onClick={() => void rowsQ.refetch()}
            data-testid="sample-preservation-refresh-button"
          >
            Muat ulang
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Baris muncul setelah ada berita acara dengan tanda tangan pelanggan dan petugas. Admin
        penerima sample menandai berita acara terbit lewat <strong>Sample received</strong> (bisa
        bertahap). Record rangkaian pengamanan dibuat pada penerimaan pertama.
      </Alert>

      {rowsQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {rowsQ.error instanceof Error ? rowsQ.error.message : 'Gagal memuat daftar.'}
        </Alert>
      ) : null}

      {!rowsQ.isPending && !rowsQ.isFetching && rows.length === 0 && !rowsQ.isError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Belum ada work order di daftar pengamanan sample. Baris muncul setelah minimal satu berita
          acara memiliki tanda tangan pelanggan dan petugas. Pastikan TTD sudah disimpan (refresh halaman
          survey), lalu klik Muat ulang.
        </Alert>
      ) : null}

      <Box sx={{ width: '100%', flex: 1, minHeight: 360 }} data-testid="sample-preservation-grid">
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={rowsQ.isPending || rowsQ.isFetching}
          disableRowSelectionOnClick
          getRowId={(r) => r.row_key}
          pageSizeOptions={DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model.length ? model : DEFAULT_PRESERVATION_SORT_MODEL);
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } },
            sorting: { sortModel: DEFAULT_PRESERVATION_SORT_MODEL },
          }}
          slots={{ pagination: DataGridNumberedPagination }}
          sx={{ flex: 1, minHeight: 360 }}
        />
      </Box>

      <Dialog
        open={Boolean(receiveRow)}
        onClose={() => {
          if (receiveM.isPending) return;
          setReceiveRow(null);
          setReceiveForms([]);
          setSelectedReceiveIds([]);
          setReceiveHasStroke(false);
        }}
        maxWidth="sm"
        fullWidth
        data-testid="sample-preservation-received-dialog"
      >
        <DialogTitle>Sample received</DialogTitle>
        <DialogContent>
          {modalError && receiveRow ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setModalError(null)}>
              {modalError}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {receiveRow?.work_order_label}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Tanggal sampling"
              type="date"
              size="small"
              fullWidth
              required
              value={samplingDate}
              onChange={(e) => setSamplingDate(e.target.value)}
              helperText="Isi tanggal di PDF RPS: Disposisi penerima sampel ke PL / PS."
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'sample-preservation-received-sampling-date' }}
            />
            <TextField
              label="Tanggal sample diterima"
              type="date"
              size="small"
              fullWidth
              required
              value={sampleReceivedDate}
              onChange={(e) => setSampleReceivedDate(e.target.value)}
              helperText="Isi tanggal di PDF RPS: Disposisi PL / PS ke analis / rpsC."
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'sample-preservation-received-sample-date' }}
            />
            <TextField
              label="Waktu diterima"
              type="datetime-local"
              size="small"
              fullWidth
              value={receivedAtLocal}
              onChange={(e) => setReceivedAtLocal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'sample-preservation-received-datetime' }}
            />
            <Autocomplete
              options={adminPenerimaOptions}
              value={receivedBy}
              onChange={(_, v) => setReceivedBy(v)}
              getOptionLabel={userLabel}
              isOptionEqualToValue={(a, b) => a.numericId === b.numericId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Diterima oleh (admin penerima sample)"
                  size="small"
                  required
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'sample-preservation-received-by',
                  }}
                />
              )}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Pengawetan dilakukan oleh
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preservationByCustomer}
                      onChange={(e) => setPreservationByCustomer(e.target.checked)}
                      data-testid="sample-preservation-received-by-customer"
                    />
                  }
                  label="Pelanggan"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preservationByLaboratory}
                      onChange={(e) => setPreservationByLaboratory(e.target.checked)}
                      data-testid="sample-preservation-received-by-laboratory"
                    />
                  }
                  label="Laboratorium"
                />
              </FormGroup>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Kondisi sampel saat diterima termasuk abnormalitas atau penyimpangan dari kondisi
                normal
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 0.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionTidakDiawetkan}
                      onChange={(e) => setConditionTidakDiawetkan(e.target.checked)}
                      data-testid="sample-preservation-received-condition-tidak-diawetkan"
                    />
                  }
                  label="tidak diawetkan di lapangan"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionKadaluarsa}
                      onChange={(e) => setConditionKadaluarsa(e.target.checked)}
                      data-testid="sample-preservation-received-condition-kadaluarsa"
                    />
                  }
                  label="sampel kedaluarsa (melampaui holding time)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={conditionWadahTidakSesuai}
                      onChange={(e) => setConditionWadahTidakSesuai(e.target.checked)}
                      data-testid="sample-preservation-received-condition-wadah"
                    />
                  }
                  label="wadah sampel tidak sesuai"
                />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={conditionLainnya}
                        onChange={(e) => {
                          setConditionLainnya(e.target.checked);
                          if (!e.target.checked) setConditionLainnyaNote('');
                        }}
                        data-testid="sample-preservation-received-condition-lainnya"
                      />
                    }
                    label="lainnya, sebutkan:"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    disabled={!conditionLainnya}
                    value={conditionLainnyaNote}
                    onChange={(e) => setConditionLainnyaNote(e.target.value)}
                    placeholder="Sebutkan kondisi lainnya"
                    inputProps={{ 'data-testid': 'sample-preservation-received-condition-lainnya-note' }}
                  />
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Kode Sampel
              </Typography>
              {receiveLoading ? (
                <Box display="flex" justifyContent="center" py={1}>
                  <CircularProgress size={24} />
                </Box>
              ) : receiveForms.length === 0 ? (
                <Alert severity="warning">
                  Belum ada berita acara pada work order ini.
                </Alert>
              ) : (
                <>
                  {receiveRow?.sample_pickup_method === 'diantar' ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {receiveOrderLocked
                        ? 'Urutan kode sampel terkunci karena ada sampel yang sudah diterima.'
                        : 'Geser ikon di kiri kode sampel untuk mengatur urutan.'}
                    </Typography>
                  ) : null}
                  {receiveForms.some(
                    (f) => f.is_fully_signed && !f.verified && !f.handed_over_to_office_at,
                  ) ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Kode sampel yang belum diverifikasi admin form sampling tidak dapat diterima.
                    </Alert>
                  ) : null}
                  <Stack spacing={0.5}>
                    {receiveForms.map((form) => {
                      const already = Boolean(form.handed_over_to_office_at);
                      const incomplete = !form.is_fully_signed || !form.berita_acara_document_id;
                      const canCheck =
                        Boolean(form.berita_acara_document_id) &&
                        !already &&
                        !incomplete &&
                        form.verified &&
                        !receiveM.isPending;
                      const canDrag =
                        canReorderReceiveCodes &&
                        Boolean(form.berita_acara_document_id) &&
                        !reorderReceiveM.isPending &&
                        !receiveM.isPending;
                      const rowKey = receiveFormKey(form);
                      const dragId = form.berita_acara_document_id;
                      return (
                        <Stack
                          key={rowKey}
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          onDragOver={(e) => {
                            if (!canDrag || !draggingReceiveId) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromId = e.dataTransfer.getData('text/plain') || draggingReceiveId || '';
                            setDraggingReceiveId(null);
                            if (!canDrag) return;
                            const next = moveReceiveForm(receiveForms, fromId, dragId);
                            if (!next) return;
                            setReceiveForms(next);
                            reorderReceiveM.mutate(next);
                          }}
                          sx={{
                            borderRadius: 1,
                            px: 0.5,
                            bgcolor: draggingReceiveId === dragId ? 'action.hover' : 'transparent',
                          }}
                        >
                          {canDrag ? (
                            <Box
                              component="span"
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', dragId);
                                e.dataTransfer.effectAllowed = 'move';
                                setDraggingReceiveId(dragId);
                              }}
                              onDragEnd={() => setDraggingReceiveId(null)}
                              sx={{
                                display: 'inline-flex',
                                cursor: draggingReceiveId === dragId ? 'grabbing' : 'grab',
                                color: 'text.secondary',
                              }}
                              aria-label={`Geser urutan ${beritaAcaraCheckboxLabel(form)}`}
                              data-testid={`sample-preservation-received-drag-${rowKey}`}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                          ) : (
                            <Box sx={{ width: 20, flexShrink: 0 }} />
                          )}
                          <FormControlLabel
                            sx={{ flex: 1, m: 0 }}
                            control={
                              <Checkbox
                                checked={
                                  already ||
                                  (!incomplete && selectedReceiveIds.includes(form.berita_acara_document_id))
                                }
                                disabled={!canCheck}
                                onChange={(e) => {
                                  const id = form.berita_acara_document_id;
                                  if (!id) return;
                                  setSelectedReceiveIds((prev) => {
                                    if (e.target.checked) return prev.includes(id) ? prev : [...prev, id];
                                    return prev.filter((x) => x !== id);
                                  });
                                }}
                                data-testid={`sample-preservation-received-ba-${rowKey}`}
                              />
                            }
                            label={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography
                                  variant="body2"
                                  color={incomplete || !form.verified ? 'text.secondary' : 'text.primary'}
                                >
                                  {beritaAcaraCheckboxLabel(form)}
                                  {already
                                    ? ' (sudah diterima)'
                                    : incomplete
                                      ? ' (belum selesai)'
                                      : ''}
                                </Typography>
                                {form.berita_acara_document_id ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={form.verified ? 'success' : 'warning'}
                                    label={form.verified ? 'Verified' : 'Belum verified'}
                                    data-testid={`sample-preservation-received-ba-verified-${rowKey}`}
                                  />
                                ) : null}
                              </Stack>
                            }
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Box>
            <SamplePreservationSignaturePad
              ref={receiveSignRef}
              open={Boolean(receiveRow)}
              disabled={receiveM.isPending}
              title="Tanda tangan admin penerima sample"
              helperText="Gambar tanda tangan petugas yang menerima sample di kantor."
              canvasTestId="sample-preservation-received-sign-canvas"
              onStrokeChange={setReceiveHasStroke}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="sample-preservation-received-cancel"
            onClick={() => {
              setReceiveRow(null);
              setReceiveForms([]);
              setSelectedReceiveIds([]);
              setReceiveHasStroke(false);
            }}
            disabled={receiveM.isPending}
          >
            Batal
          </Button>
          <Button
            variant="contained"
            data-testid="sample-preservation-received-submit"
            disabled={
              receiveM.isPending ||
              receiveLoading ||
              receivedBy?.numericId == null ||
              selectedReceiveIds.length === 0 ||
              !samplingDate.trim() ||
              !sampleReceivedDate.trim() ||
              !receiveHasStroke
            }
            onClick={() => {
              setModalError(null);
              receiveM.mutate();
            }}
          >
            {receiveM.isPending ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>

      <SamplePreservationCombinedPdfPreviewDialog
        open={Boolean(combinedPdfRow)}
        workOrderDocumentId={combinedPdfRow?.work_order_document_id ?? null}
        workOrderLabel={combinedPdfRow?.work_order_label || combinedPdfRow?.work_order_job_number}
        jwt={jwt}
        forceFresh={combinedPdfRow?.rps_status === 'approved'}
        onClose={() => setCombinedPdfRow(null)}
      />

      <SurveyBeritaAcaraPdfPreviewDialog
        open={Boolean(beritaAcaraPdfRow)}
        workOrderDocumentId={beritaAcaraPdfRow?.work_order_document_id ?? null}
        workOrderLabel={beritaAcaraPdfRow?.work_order_label || beritaAcaraPdfRow?.work_order_job_number}
        jwt={jwt}
        canSendEmail
        defaultRecipientEmail={beritaAcaraPdfRow?.customer_email ?? ''}
        signedPointCount={beritaAcaraPdfRow?.signed_points ?? 0}
        onClose={() => setBeritaAcaraPdfRow(null)}
      />

      <SamplePreservationReceiveLogsDialog
        open={Boolean(receiveLogsRow)}
        workOrderDocumentId={receiveLogsRow?.work_order_document_id ?? null}
        workOrderLabel={receiveLogsRow?.work_order_label || receiveLogsRow?.work_order_job_number}
        jwt={jwt}
        onClose={() => setReceiveLogsRow(null)}
      />

      <VerifyFormSamplingDialog
        row={verifyFormRow}
        readOnly={verifyFormReadOnly}
        onClose={() => {
          setVerifyFormRow(null);
          setVerifyFormReadOnly(false);
          void invalidate();
        }}
        onVerifiedChange={invalidate}
      />

      <Dialog
        open={Boolean(rpsConfirm)}
        onClose={() => {
          if (rpsReviewM.isPending) return;
          setRpsConfirm(null);
          setRpsRejectNote('');
          setRpsApproveHasStroke(false);
          setRpsActionError(null);
        }}
        fullWidth
        maxWidth="sm"
        data-testid="sample-preservation-rps-review-dialog"
      >
        <DialogTitle>{rpsConfirm?.action === 'reject' ? 'Tolak RPS' : 'Setujui RPS'}</DialogTitle>
        <DialogContent>
          {rpsActionError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setRpsActionError(null)}>
              {rpsActionError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {rpsConfirm?.action === 'reject'
              ? `Tolak RPS untuk ${rpsConfirm.row.work_order_label || rpsConfirm.row.work_order_job_number}?`
              : `Setujui RPS untuk ${rpsConfirm?.row.work_order_label || rpsConfirm?.row.work_order_job_number}?`}
          </Typography>
          {rpsConfirm?.action === 'reject' ? (
            <TextField
              label="Catatan penolakan"
              size="small"
              fullWidth
              required
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={rpsRejectNote}
              onChange={(e) => setRpsRejectNote(e.target.value)}
              inputProps={{ 'data-testid': 'sample-preservation-rps-reject-note' }}
            />
          ) : (
            <Box sx={{ mt: 2 }}>
              <SamplePreservationSignaturePad
                ref={rpsApproveSignRef}
                open={rpsConfirm?.action === 'approve'}
                disabled={rpsReviewM.isPending}
                title="Tanda tangan SPV laboratorium"
                helperText="Paraf penyelia di PDF RPS memakai tanda tangan ini."
                canvasTestId="sample-preservation-approve-rps-sign-canvas"
                onStrokeChange={setRpsApproveHasStroke}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRpsConfirm(null);
              setRpsRejectNote('');
              setRpsApproveHasStroke(false);
              setRpsActionError(null);
            }}
            disabled={rpsReviewM.isPending}
          >
            Batal
          </Button>
          <Button
            variant="contained"
            color={rpsConfirm?.action === 'reject' ? 'error' : 'primary'}
            disabled={
              rpsReviewM.isPending ||
              (rpsConfirm?.action === 'reject' && !rpsRejectNote.trim()) ||
              (rpsConfirm?.action === 'approve' && !rpsApproveHasStroke)
            }
            onClick={() => rpsReviewM.mutate()}
            data-testid="sample-preservation-rps-review-confirm"
          >
            {rpsReviewM.isPending ? 'Memproses…' : 'Ya, lanjutkan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
